import { useEffect, useRef, useState } from "react";

import * as Lucide from "lucide-react";

import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { Button } from "@/components/ui/button";

const formatTimeRemaining = (duration: number, timeInSeconds: number) => {
  const timeRemaining = Math.max(0, duration - timeInSeconds);
  const minutes = Math.floor(timeRemaining / 60);
  const seconds = Math.floor(timeRemaining % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

function IntroductionAudioButton({
  initialIsPlaying,
  audioUrl,
  onFinished,
}: {
  initialIsPlaying?: boolean;
  audioUrl: string;
  onFinished: () => void;
}) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(initialIsPlaying ?? false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  // Forces a reload of the audio by temporarily clearing and resetting the source
  useEffect(() => {
    if (audioRef.current) {
      const audio = audioRef.current;

      if (audio.duration && audio.duration !== Infinity) {
        setDuration(audio.duration);
      }

      const currentSrc = audio.src;
      audio.src = "";
      audio.src = currentSrc;
    }
  }, [audioUrl]);

  const togglePlay = () => {
    if (!audioRef.current) {
      return;
    }
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {
        onFinished();
      });
    }
    setIsPlaying(!isPlaying);
  };

  const onReplay = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        onFinished();
      });
      setIsPlaying(true);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      // If duration is still 0, try to get it during playback
      if (
        duration === 0 &&
        audioRef.current.duration &&
        audioRef.current.duration !== Infinity
      ) {
        setDuration(audioRef.current.duration);
      }
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    onFinished();
  };

  return (
    <div>
      {!isPlaying ? (
        <Button
          className="w-[200px]"
          intent="primary"
          variant="solid"
          size="lg"
          onClick={togglePlay}
        >
          <Lucide.Phone fill="white" strokeWidth={0} size={16} color="white" />
          Begin Session
        </Button>
      ) : (
        <div className="grid grid-flow-col gap-2 items-center">
          <Button
            className="w-[200px]"
            size="lg"
            onClick={onFinished}
            intent="secondary"
          >
            Skip Intro
          </Button>
          <Tooltip content="Replay" placement="top">
            <Button size="lg" onClick={onReplay} intent="secondary">
              <Lucide.RefreshCcw />
            </Button>
          </Tooltip>
          <div>{formatTimeRemaining(duration, currentTime)}</div>
        </div>
      )}
      <audio
        style={{ display: "none" }}
        onLoadedMetadata={handleLoadedMetadata}
        ref={audioRef}
        src={audioUrl}
        onEnded={handleEnded}
        onTimeUpdate={handleTimeUpdate}
        controls
      />
    </div>
  );
}

export default IntroductionAudioButton;
